import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Operaciones, ParDeTokens, RetoDosPasos, Yo } from '../api/operaciones';
import { ErrorApi } from '../api/problema';
import { AltaNegocio } from './AltaNegocio';
import { PasoDelAlta } from './InvitacionHoja';
import { tokenCaptcha } from './InvitacionCaptcha';
import { ProtegerCuenta } from './ProtegerCuenta';
import { Asterisco } from '../componentes/Asterisco';
import { Campo } from '../componentes/Campo';
import { CodigoSeis } from '../componentes/CodigoSeis';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import asterisco from '../estilo/comun/assets/asterisco-logo.svg';
import logo from '../estilo/comun/assets/wordmark-for-red-cropped.png';
import {
    MENSAJES,
    TEXTOS_VERIFICAR,
    completarDosPasos,
    emailValido,
    entrar,
    errorDeCampo,
    fuerzaContrasena,
    mensajeDeError,
    rolesDe,
    textoFuerza,
    validarEntrada,
    validarNuevaContrasena,
    validarSegundoPaso,
    type PantallaAcceso,
} from '../logica/acceso';
import { PASOS_ALTA, TEXTOS_CUENTA, enAlta, siguienteTrasVerificar, validarCuenta, type ErroresCuenta } from '../logica/altaCuenta';
import type { EleccionNegocio } from '../logica/sesionPanel';
import { lineaNegocio, iniciales } from '../logica/navegacion';
import { EtiquetaEstado } from '../marco/piezas';
import { useEsMovil } from '../marco/tamano';

/**
 * Hoja de acceso roja (componentes.md §16; pantallas 1.3 y 1.4 de diseno/plataforma): iniciar sesión, dos pasos, código
 * de recuperación, contraseña olvidada (pedir enlace, enlace enviado, contraseña nueva, hecho), elegir negocio y Soporte
 * sin dos pasos. Textos: los del diseño y los aprobados (G122, G123, G126). Fondo: el fondo vivo de la exportación con el
 * asterisco grande (decisión del equipo del 2026-10-21), quieto con «reducir movimiento».
 *
 * El alta de un propietario nuevo (tarea 490), en el orden del dibujo: «Crear cuenta» (1.1, `createAccount` con antibot, y
 * se entra con esa cuenta) → «Revisa tu correo» y «Email verificado» (1.2, «Paso 2 de 4») → «Protege tu cuenta» (1.6,
 * `ProtegerCuenta`, sin obligar) → «Tu negocio» (1.5, `AltaNegocio`, «Paso 4 de 4») → el panel. «Sin negocio» › «Crear mi
 * negocio» abre también «Tu negocio». Fuera de esta entrega (esperan decisión): Google y Apple (G100–G111, ADR-0043) y
 * activar los dos pasos de Soporte (1.6 desde `force2fa`).
 */
const ORDEN: Record<PantallaAcceso, number> = { signup: 3, login: 12, force2fa: 18, twofa: 20, biz: 21, forgot: 22, forgotSent: 23, reset: 24, resetDone: 25, verify: 14, offer2fa: 9, newbiz: 11 };

/** Verificar el email (1.2): comprobando el enlace, hecho, caducado, «Revisa tu correo» (sin enlace) y corregir el email. */
type EstadoVerificar = 'comprobando' | 'verificado' | 'caducado' | 'pendiente' | 'cambiar';

interface Props {
    pantalla: PantallaAcceso;
    ops: Operaciones;
    /** Token del enlace del email (`?token=`), para la contraseña nueva o para verificar el email. */
    tokenNueva: string | null;
    /** Hay sesión abierta (verificar el email: reenviar el enlace o corregirlo, que piden sesión). */
    conSesion?: boolean;
    /** Tras verificar el email: al panel (con sesión) o a entrar. */
    onContinuar?: () => void;
    eleccion: EleccionNegocio | null;
    onIr: (pantalla: PantallaAcceso) => void;
    onSesion: (tokens: ParDeTokens) => void;
    /** Cierra la sesión (Soporte sin dos pasos: «Volver»). */
    onSalir: () => void;
}

interface Hoja {
    pestanas?: boolean;
    volver?: () => void;
    baldosa?: [NombreIcono, string, string];
    /** «Paso N de 4» del alta (tarea 490). */
    paso?: number;
    titulo?: string;
    sub?: string;
    principal?: { texto: string; icono: NombreIcono; ocupado: string; accion: () => void; desactivado?: boolean };
    cuerpo?: ReactNode;
    enlaces?: { antes?: string; texto: string; accion?: () => void; desactivado?: boolean }[];
    pie?: ReactNode;
}

export function Acceso({ pantalla, ops, tokenNueva, conSesion = false, onContinuar, eleccion, onIr, onSesion, onSalir }: Props) {
    const movil = useEsMovil();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    // Errores de la pantalla en la que se produjeron: al cambiar de pantalla (también con «atrás» del navegador) no se ven.
    const [fallos, setFallos] = useState<{ pantalla: PantallaAcceso; campos: { email?: string; password?: string; nueva?: string; repetida?: string }; form: string | null }>({ pantalla, campos: {}, form: null });
    const errores = fallos.pantalla === pantalla ? fallos.campos : {};
    const errorForm = fallos.pantalla === pantalla ? fallos.form : null;
    const setErrores = (campos: typeof fallos.campos) => {
        setFallos((f) => ({ pantalla, campos, form: f.pantalla === pantalla ? f.form : null }));
    };
    const setErrorForm = (form: string | null) => {
        setFallos((f) => ({ pantalla, campos: f.pantalla === pantalla ? f.campos : {}, form }));
    };
    const [ocupado, setOcupado] = useState(false);
    const [reto, setReto] = useState<RetoDosPasos | null>(null);
    const [recuperacion, setRecuperacion] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [nueva, setNueva] = useState('');
    const [repetida, setRepetida] = useState('');
    const [espera, setEspera] = useState(0);
    const [eligiendo, setEligiendo] = useState<string | null>(null);
    const [verificar, setVerificar] = useState<EstadoVerificar>(tokenNueva !== null && tokenNueva !== '' ? 'comprobando' : 'pendiente');
    const [cuenta, setCuenta] = useState<Yo | null>(null);
    const [emailNuevo, setEmailNuevo] = useState('');
    const enlaceUsado = useRef<string | null>(null);
    // «Crear mi negocio» desde «Sin negocio» (G132) abre «Tu negocio» (1.5).
    const [creandoNegocio, setCreandoNegocio] = useState(false);
    // «Crear cuenta» (1.1): nombre, errores propios, antibot no superado y el email con que se creó (para «Revisa tu correo»).
    const [nombre, setNombre] = useState('');
    const [erroresCuenta, setErroresCuenta] = useState<ErroresCuenta>({});
    const [robot, setRobot] = useState(false);
    const [emailAlta, setEmailAlta] = useState('');

    // 1.2: el enlace del email se confirma una sola vez (también con StrictMode); con sesión, se lee la cuenta (su email).
    useEffect(() => {
        if (pantalla !== 'verify') {
            return;
        }
        if (tokenNueva !== null && tokenNueva !== '' && enlaceUsado.current !== tokenNueva) {
            enlaceUsado.current = tokenNueva;
            ops.confirmEmailVerification(tokenNueva)
                .then(() => {
                    setVerificar('verificado');
                })
                .catch((e: unknown) => {
                    // Caducado o ya usado (`422 code_invalid`, G134): «Este enlace ha caducado». Otro fallo se cuenta además.
                    setVerificar('caducado');
                    if (!(e instanceof ErrorApi && e.codigo === 'code_invalid')) {
                        setErrorForm(mensajeDeError(e, 'contrasena'));
                    }
                });
        }
        if (conSesion) {
            ops.getMe().then(setCuenta).catch(() => undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir la pantalla o cambiar el enlace
    }, [pantalla, tokenNueva, conSesion, ops]);

    // «Reenviar en N s»: cuenta atrás de 60 s tras pedir el enlace.
    useEffect(() => {
        if (espera <= 0) {
            return;
        }
        const t = setTimeout(() => {
            setEspera((n) => Math.max(0, n - 1));
        }, 1000);
        return () => {
            clearTimeout(t);
        };
    }, [espera]);

    // Sin reto (p. ej. al recargar), el segundo paso vuelve a entrar.
    useEffect(() => {
        if (pantalla === 'twofa' && reto === null) {
            onIr('login');
        }
    }, [pantalla, reto, onIr]);

    const ir = (p: PantallaAcceso) => {
        setOcupado(false);
        onIr(p);
    };

    const trabaja = async (accion: () => Promise<void>, paso: Parameters<typeof mensajeDeError>[1]) => {
        setOcupado(true);
        setErrorForm(null);
        try {
            await accion();
        } catch (e) {
            setErrorForm(mensajeDeError(e, paso));
            setOcupado(false);
        }
    };

    const enviarEntrada = () => {
        const fallos = validarEntrada(email, password);
        setErrores(fallos);
        if (fallos.email !== undefined || fallos.password !== undefined) {
            return;
        }
        void trabaja(async () => {
            const r = await entrar(ops, email, password);
            if (r.tipo === 'dos-pasos') {
                setReto(r.reto);
                setCodigo('');
                setRecuperacion(false);
                setOcupado(false);
                ir('twofa');
            } else {
                onSesion(r.tokens);
            }
        }, 'entrar');
    };

    const enviarSegundoPaso = (valor = codigo) => {
        const fallo = validarSegundoPaso(recuperacion, valor);
        if (fallo !== null) {
            setErrorForm(fallo);
            return;
        }
        if (reto === null) {
            return;
        }
        void trabaja(async () => {
            onSesion(await completarDosPasos(ops, reto, recuperacion, valor));
        }, 'dos-pasos');
    };

    const pedirEnlace = () => {
        const fallos = validarEntrada(email, 'x');
        setErrores({ email: fallos.email });
        if (fallos.email !== undefined) {
            return;
        }
        void trabaja(async () => {
            await ops.requestPasswordReset(email.trim());
            empiezaEspera();
            setOcupado(false);
            ir('forgotSent');
        }, 'contrasena');
    };

    const empiezaEspera = () => {
        setEspera(60);
    };

    const guardarNueva = () => {
        const fallos = validarNuevaContrasena(nueva, repetida);
        setErrores(fallos);
        if (fallos.nueva !== undefined || fallos.repetida !== undefined) {
            return;
        }
        setOcupado(true);
        setErrorForm(null);
        ops.confirmPasswordReset({ token: tokenNueva ?? '', new_password: nueva })
            .then(() => {
                setOcupado(false);
                ir('resetDone');
            })
            .catch((e: unknown) => {
                const delCampo = errorDeCampo(e, 'new_password');
                if (delCampo !== null) {
                    setErrores({ nueva: delCampo });
                } else {
                    setErrorForm(mensajeDeError(e, 'contrasena'));
                }
                setOcupado(false);
            });
    };

    // 1.1: crea la cuenta (con antibot, G76) y entra con ella, sin negocio: la sesión lleva a «Revisa tu correo» (1.2, G66,
    // G168). Si el email ya tenía cuenta, la API responde igual y avisa a ese email (no se revela): no se puede entrar con
    // esta contraseña y se enseña «Revisa tu correo» sin sesión.
    const crearCuenta = () => {
        const fallos = validarCuenta(nombre, email, password);
        setErroresCuenta(fallos);
        if (Object.keys(fallos).length > 0) {
            return;
        }
        setOcupado(true);
        setErrorForm(null);
        setRobot(false);
        void (async () => {
            try {
                await ops.createAccount({ display_name: nombre.trim(), email: email.trim(), password }, await tokenCaptcha('alta'));
            } catch (e) {
                setOcupado(false);
                if (e instanceof ErrorApi && e.codigo === 'captcha_required') {
                    setRobot(true);
                    return;
                }
                const delCampo = errorDeCampo(e, 'password');
                if (delCampo !== null) {
                    setErroresCuenta({ password: delCampo });
                    return;
                }
                setErrorForm(mensajeDeError(e, 'contrasena'));
                return;
            }
            setEmailAlta(email.trim());
            empiezaEspera();
            try {
                const r = await entrar(ops, email, password);
                if (r.tipo === 'dos-pasos') {
                    setReto(r.reto);
                    setCodigo('');
                    setRecuperacion(false);
                    ir('twofa');
                    return;
                }
                setOcupado(false);
                onSesion(r.tokens);
            } catch {
                setVerificar('pendiente');
                ir('verify');
            }
        })();
    };

    const fuerza = fuerzaContrasena(pantalla === 'signup' ? password : nueva);
    const colorFuerza = ['var(--rv-paper)', 'var(--rv-red)', 'var(--rv-warning)', 'var(--rv-ink)'][fuerza] ?? 'var(--rv-paper)';
    const medidor = (valor: string) => (
        <div className="rv-acceso__medidor" aria-live="polite">
            <div>
                {[1, 2, 3].map((n) => (
                    <span key={n} style={{ background: n <= fuerza ? colorFuerza : 'var(--rv-paper)' }} />
                ))}
            </div>
            <span style={{ color: fuerza === 1 ? 'var(--rv-red)' : 'var(--rv-fg-3)' }}>{textoFuerza(valor)}</span>
        </div>
    );
    // «Paso 2 de 4» en 1.2 solo durante el alta (cuenta del equipo de un negocio sin ninguno todavía).
    const pasoVerificar = conSesion && enAlta(cuenta) ? PASOS_ALTA.verificar : undefined;

    const emailCuenta = cuenta?.account.pending_email ?? cuenta?.account.email ?? emailAlta;
    const reenviarVerificacion = () => {
        void trabaja(async () => {
            await ops.sendEmailVerification();
            empiezaEspera();
            setOcupado(false);
            setVerificar('pendiente');
        }, 'contrasena');
    };
    const corregirEmail = () => {
        if (!emailValido(emailNuevo)) {
            setErrores({ email: MENSAJES.emailNoValido });
            return;
        }
        setErrores({});
        void trabaja(async () => {
            await ops.startEmailChange(emailNuevo.trim());
            setCuenta(await ops.getMe());
            empiezaEspera();
            setOcupado(false);
            setVerificar('pendiente');
        }, 'contrasena');
    };
    const hojaVerificar = (): Hoja => {
        switch (verificar) {
            case 'comprobando':
                return { titulo: TEXTOS_VERIFICAR.comprobando, cuerpo: <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--rv-red)' }}><Asterisco tamano={40} /></div> };
            case 'verificado':
                return {
                    baldosa: ['circle-check', 'var(--rv-red)', '#fff'],
                    titulo: TEXTOS_VERIFICAR.verificado,
                    paso: pasoVerificar,
                    // «Ahora, protégela y crea tu negocio» es del alta: solo para quien aún no tiene negocio.
                    sub: enAlta(cuenta) ? TEXTOS_VERIFICAR.verificadoSub : undefined,
                    principal: {
                        texto: 'Continuar',
                        icono: 'arrow-right',
                        ocupado: 'Un momento…',
                        accion: () => {
                            // En el alta sigue a «Protege tu cuenta» (1.6) y luego a «Tu negocio» (1.5), como el dibujo.
                            const siguiente = conSesion ? siguienteTrasVerificar(cuenta) : null;
                            if (siguiente !== null) {
                                ir(siguiente);
                            } else if (conSesion && onContinuar !== undefined) {
                                onContinuar();
                            } else {
                                ir('login');
                            }
                        },
                    },
                };
            case 'caducado':
                return {
                    baldosa: ['clock', 'var(--rv-paper)', 'var(--rv-ink)'],
                    titulo: TEXTOS_VERIFICAR.caducado,
                    sub: conSesion && emailCuenta !== '' ? `${TEXTOS_VERIFICAR.caducadoSub} ${TEXTOS_VERIFICAR.caducadoNuevo(emailCuenta)}` : TEXTOS_VERIFICAR.caducadoSub,
                    // Pedir otro enlace necesita sesión: sin ella, se entra primero y la entrada trae aquí (G168).
                    principal: conSesion
                        ? { texto: TEXTOS_VERIFICAR.enlaceNuevo, icono: 'send', ocupado: 'Enviando…', accion: reenviarVerificacion }
                        : { texto: 'Iniciar sesión', icono: 'log-in', ocupado: 'Un momento…', accion: () => { ir('login'); } },
                    enlaces: conSesion ? [{ antes: TEXTOS_VERIFICAR.otroEmail, texto: TEXTOS_VERIFICAR.cambiarlo, accion: () => { setVerificar('cambiar'); } }] : undefined,
                };
            case 'cambiar':
                return {
                    volver: () => {
                        setVerificar('pendiente');
                    },
                    paso: pasoVerificar,
                    titulo: TEXTOS_VERIFICAR.corrige,
                    sub: TEXTOS_VERIFICAR.corrigeSub,
                    principal: { texto: TEXTOS_VERIFICAR.enviarEnlace, icono: 'send', ocupado: 'Enviando…', accion: corregirEmail },
                    cuerpo: (
                        <div className="rv-acceso__campo">
                            <Campo etiqueta="Email" tipo="email" autocompletar="email" valor={emailNuevo} error={errores.email} onChange={setEmailNuevo} onEnter={corregirEmail} />
                        </div>
                    ),
                };
            case 'pendiente':
                return {
                    volver: onSalir,
                    baldosa: ['mail', 'var(--rv-ink)', '#fff'],
                    paso: pasoVerificar,
                    titulo: TEXTOS_VERIFICAR.revisa,
                    sub: emailCuenta === '' ? undefined : TEXTOS_VERIFICAR.revisaSub(emailCuenta),
                    enlaces: conSesion
                        ? [
                              {
                                  antes: TEXTOS_VERIFICAR.noHaLlegado,
                                  texto: espera > 0 ? `Reenviar en ${String(espera)} s` : TEXTOS_VERIFICAR.reenviar,
                                  desactivado: espera > 0,
                                  accion: reenviarVerificacion,
                              },
                              { antes: TEXTOS_VERIFICAR.otroEmail, texto: TEXTOS_VERIFICAR.cambiarlo, accion: () => { setVerificar('cambiar'); } },
                          ]
                        : [{ texto: 'Iniciar sesión', accion: () => { ir('login'); } }],
                };
        }
    };

    const cargando: Hoja = { titulo: 'Hola', cuerpo: <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--rv-red)' }}><Asterisco tamano={40} /></div> };
    const hojas: Record<PantallaAcceso, Hoja> = {
        signup: {
            pestanas: true,
            principal: { texto: TEXTOS_CUENTA.crear, icono: 'arrow-right', ocupado: TEXTOS_CUENTA.comprobando, accion: crearCuenta },
            cuerpo: (
                <div className="rv-acceso__campos">
                    <div className="rv-acceso__campo" style={{ animationDelay: '60ms' }}>
                        <Campo etiqueta="Nombre y apellidos" autocompletar="name" mayusculas="words" valor={nombre} error={erroresCuenta.nombre} onChange={setNombre} />
                    </div>
                    <div className="rv-acceso__campo" style={{ animationDelay: '105ms' }}>
                        <Campo etiqueta="Email" tipo="email" autocompletar="email" valor={email} error={erroresCuenta.email} onChange={setEmail} />
                    </div>
                    <div className="rv-acceso__campo" style={{ animationDelay: '150ms' }}>
                        <Campo etiqueta="Contraseña" tipo="password" autocompletar="new-password" valor={password} error={erroresCuenta.password} onChange={setPassword} onEnter={crearCuenta} />
                        {erroresCuenta.password === undefined && medidor(password)}
                    </div>
                </div>
            ),
            pie: (
                <div className="rv-alta__legal">
                    {robot && (
                        <div role="alert" className="rv-acceso__error">
                            <Icono nombre="shield-alert" />
                            <span>{TEXTOS_CUENTA.robot}</span>
                        </div>
                    )}
                    {/* Enlaces de las condiciones y la privacidad de Reservaria: pendientes de su dirección y versión (G188). */}
                    <span>
                        Al crear la cuenta aceptas las <u>condiciones del servicio</u> y la <u>política de privacidad</u>.
                    </span>
                    <span>
                        Este sitio está protegido por reCAPTCHA y se aplican la{' '}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                            Política de privacidad
                        </a>{' '}
                        y las{' '}
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                            Condiciones del servicio
                        </a>{' '}
                        de Google.
                    </span>
                </div>
            ),
        },
        offer2fa: cargando,
        newbiz: cargando,
        login: {
            pestanas: true,
            principal: { texto: 'Entrar', icono: 'log-in', ocupado: 'Entrando…', accion: enviarEntrada },
            cuerpo: (
                <div className="rv-acceso__campos">
                    <div className="rv-acceso__campo" style={{ animationDelay: '60ms' }}>
                        <Campo etiqueta="Email" tipo="email" autocompletar="username" valor={email} error={errores.email} onChange={setEmail} onEnter={enviarEntrada} />
                    </div>
                    <div className="rv-acceso__campo" style={{ animationDelay: '105ms' }}>
                        <Campo etiqueta="Contraseña" tipo="password" autocompletar="current-password" valor={password} error={errores.password} onChange={setPassword} onEnter={enviarEntrada} />
                        <button type="button" className="rv-acceso__aparte" onClick={() => { ir('forgot'); }}>
                            ¿Has olvidado tu contraseña?
                        </button>
                    </div>
                </div>
            ),
        },
        twofa: {
            volver: () => {
                ir('login');
            },
            titulo: recuperacion ? 'Código de recuperación' : 'Verificación en dos pasos',
            sub: recuperacion ? 'Uno de los que guardaste. Cada uno sirve una vez.' : 'Escribe los 6 números de tu app de autenticación.',
            principal: { texto: 'Verificar', icono: 'shield-check', ocupado: 'Comprobando…', accion: () => { enviarSegundoPaso(); } },
            cuerpo: recuperacion ? (
                <div className="rv-acceso__campo">
                    <Campo etiqueta="Código de recuperación" autocompletar="one-time-code" mayusculas="none" valor={codigo} onChange={setCodigo} onEnter={() => { enviarSegundoPaso(); }} />
                </div>
            ) : (
                <div className="rv-acceso__campo">
                    <CodigoSeis valor={codigo} onChange={setCodigo} error={errorForm !== null} autoFocus onCompleto={enviarSegundoPaso} />
                </div>
            ),
            enlaces: [
                {
                    texto: recuperacion ? 'Usar la aplicación' : 'Usar un código de recuperación',
                    accion: () => {
                        setRecuperacion((r) => !r);
                        setCodigo('');
                        setErrorForm(null);
                    },
                },
            ],
        },
        force2fa: cargando,
        biz:
            eleccion === null
                ? cargando
                : eleccion.aviso === MENSAJES.sinNegocio
                  ? {
                        baldosa: ['store', 'var(--rv-paper)', 'var(--rv-ink)'],
                        titulo: eleccion.saludo,
                        sub: MENSAJES.sinNegocio,
                        enlaces: [
                            // G132 (ADR-0053): «Crear mi negocio» como enlace secundario, debajo del texto de G122; abre «Tu negocio» (1.5).
                            { texto: 'Crear mi negocio', accion: () => { setCreandoNegocio(true); } },
                            { texto: 'Entrar con otra cuenta', accion: eleccion.salir },
                        ],
                    }
                  : eleccion.aviso !== null
                    ? { titulo: eleccion.saludo, enlaces: [{ texto: 'Entrar con otra cuenta', accion: eleccion.salir }] }
                    : {
                          titulo: eleccion.saludo,
                          sub: '¿En qué negocio entras?',
                          cuerpo: (
                              <div className="rv-negocios">
                                  {eleccion.negocios.map((m, i) => {
                                      const esta = eligiendo === m.business_id;
                                      return (
                                          <button
                                              key={m.business_id}
                                              type="button"
                                              className={`rv-negocio${esta ? ' rv-negocio--ocupado' : ''}`}
                                              style={{ animationDelay: `${String(120 + i * 70)}ms` }}
                                              disabled={eligiendo !== null}
                                              onClick={() => {
                                                  setEligiendo(m.business_id);
                                                  setErrorForm(null);
                                                  eleccion.elegir(m.business_id).catch((e: unknown) => {
                                                      setEligiendo(null);
                                                      setErrorForm(mensajeDeError(e, 'negocio'));
                                                  });
                                              }}
                                          >
                                              <span className="rv-avatar" style={{ width: 44, height: 44, background: 'var(--rv-red)', color: '#fff', fontSize: 14 }}>
                                                  {iniciales(m.business_name)}
                                              </span>
                                              <span className="rv-negocio__textos">
                                                  <span className="rv-negocio__nombre">{m.business_name}</span>
                                                  <span className="rv-negocio__linea">{lineaNegocio(rolesDe(m), m.public_url)}</span>
                                              </span>
                                              <EtiquetaEstado estado={m.subscription_status} />
                                              {esta ? <Asterisco /> : <Icono nombre="chevron-right" />}
                                          </button>
                                      );
                                  })}
                              </div>
                          ),
                      },
        forgot: {
            volver: () => {
                ir('login');
            },
            titulo: 'Recupera tu contraseña',
            sub: 'Te enviamos un enlace para crear una nueva.',
            principal: { texto: 'Enviar el enlace', icono: 'send', ocupado: 'Enviando…', accion: pedirEnlace },
            cuerpo: (
                <div className="rv-acceso__campo">
                    <Campo etiqueta="Email" tipo="email" autocompletar="username" valor={email} error={errores.email} onChange={setEmail} onEnter={pedirEnlace} />
                </div>
            ),
        },
        forgotSent: {
            volver: () => {
                ir('login');
            },
            baldosa: ['mail', 'var(--rv-ink)', '#fff'],
            titulo: 'Revisa tu correo',
            sub: `Si hay una cuenta con ${email.trim()}, te llegará un enlace. Caduca en una hora.`,
            enlaces: [
                {
                    antes: '¿No te ha llegado?',
                    texto: espera > 0 ? `Reenviar en ${String(espera)} s` : 'Reenviar el enlace',
                    desactivado: espera > 0,
                    accion: () => {
                        ops.requestPasswordReset(email.trim())
                            .then(empiezaEspera)
                            .catch((e: unknown) => {
                                setErrorForm(mensajeDeError(e, 'contrasena'));
                            });
                    },
                },
            ],
        },
        reset: {
            titulo: 'Contraseña nueva',
            sub: 'Se cerrarán tus otras sesiones.',
            principal: { texto: 'Guardar contraseña', icono: 'check', ocupado: 'Guardando…', accion: guardarNueva },
            cuerpo: (
                <div className="rv-acceso__campos">
                    <div className="rv-acceso__campo" style={{ animationDelay: '60ms' }}>
                        <Campo etiqueta="Contraseña nueva" tipo="password" autocompletar="new-password" valor={nueva} error={errores.nueva} onChange={setNueva} />
                        {medidor(nueva)}
                    </div>
                    <div className="rv-acceso__campo" style={{ animationDelay: '105ms' }}>
                        <Campo etiqueta="Repítela" tipo="password" autocompletar="new-password" valor={repetida} error={errores.repetida} onChange={setRepetida} onEnter={guardarNueva} />
                    </div>
                </div>
            ),
        },
        verify: hojaVerificar(),
        resetDone: {
            baldosa: ['circle-check', 'var(--rv-red)', '#fff'],
            titulo: 'Contraseña cambiada',
            sub: 'Usa tu contraseña nueva para iniciar sesión.',
            principal: { texto: 'Iniciar sesión', icono: 'log-in', ocupado: 'Un momento…', accion: () => { ir('login'); } },
        },
    };

    if (pantalla === 'biz' && creandoNegocio && eleccion !== null) {
        return <AltaNegocio ops={ops} onVolver={() => { setCreandoNegocio(false); }} onCreado={eleccion.elegir} />;
    }
    // Alta de un propietario nuevo: «Protege tu cuenta» (1.6) y después «Tu negocio» (1.5), que al crearlo entra en él.
    if (pantalla === 'offer2fa') {
        return <ProtegerCuenta ops={ops} onSeguir={() => { ir('newbiz'); }} />;
    }
    // Soporte o Superadmin sin los dos pasos (G65, G123): activarlos es obligatorio (1.6); después, al backoffice.
    if (pantalla === 'force2fa') {
        return <ProtegerCuenta ops={ops} obligatoria={{ onSalir }} onSeguir={() => { onContinuar?.(); }} />;
    }
    if (pantalla === 'newbiz' && eleccion !== null) {
        return <AltaNegocio ops={ops} paso={PASOS_ALTA.negocio} onCreado={eleccion.elegir} />;
    }

    const h = hojas[pantalla];
    const conPestanas = h.pestanas === true;
    const tabIdx = conPestanas && pantalla === 'login' ? 1 : 0;
    const giro = ORDEN[pantalla] * 60 + tabIdx * 30 + 12;

    return (
        <div className="rv-acceso">
            {/* Fondo vivo (componentes.md §1h; aceptado por el equipo el 2026-10-21): rojo que se mueve despacio. */}
            <div className="rv-vivo" aria-hidden="true" />
            <div aria-hidden="true" className="rv-acceso__hueco" style={{ marginTop: -28 }} />
            <span
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: movil ? '150vw' : 'min(120vh, 1200px)',
                    height: movil ? '150vw' : 'min(120vh, 1200px)',
                    background: 'var(--rv-red-dark)',
                    opacity: 0.6,
                    filter: 'drop-shadow(0 30px 40px rgba(0,0,0,0.25))',
                    WebkitMask: `url("${asterisco}") center/contain no-repeat`,
                    mask: `url("${asterisco}") center/contain no-repeat`,
                    transform: `translate(-50%, -50%) translate(${tabIdx === 1 ? '-22%' : '22%'}, 20%) rotate(${String(giro)}deg)`,
                    transition: 'transform 1000ms cubic-bezier(0.16,1,0.3,1)',
                    pointerEvents: 'none',
                }}
            />
            <img className="rv-acceso__logo" src={logo} alt="Reservaria" />
            <div className="rv-acceso__caja">
                <div className="rv-acceso__pestanas" role={conPestanas ? 'tablist' : undefined}>
                    {conPestanas && (
                        <>
                            {/* «Crear cuenta» (1.1) e «Iniciar sesión» (1.3): cada pestaña lleva a su pantalla. */}
                            <button type="button" role="tab" aria-selected={pantalla === 'signup'} className="rv-acceso__pestana" onClick={() => { if (pantalla !== 'signup') { ir('signup'); } }}>
                                Crear cuenta
                            </button>
                            <button type="button" role="tab" aria-selected={pantalla === 'login'} className="rv-acceso__pestana" onClick={() => { if (pantalla !== 'login') { ir('login'); } }}>
                                Iniciar sesión
                            </button>
                        </>
                    )}
                    {h.volver !== undefined && (
                        <button type="button" className="rv-acceso__volver" onClick={h.volver}>
                            <Icono nombre="chevron-left" />
                            <span>Volver</span>
                        </button>
                    )}
                </div>
                <div className="rv-acceso__hoja">
                    {h.baldosa !== undefined && (
                        <span className="rv-acceso__baldosa" style={{ background: h.baldosa[1], color: h.baldosa[2] }}>
                            <Icono nombre={h.baldosa[0]} />
                        </span>
                    )}
                    {h.paso !== undefined && <PasoDelAlta paso={h.paso} />}
                    {h.titulo !== undefined && <h1 className="rv-acceso__titulo">{h.titulo}</h1>}
                    {h.sub !== undefined && <div className="rv-acceso__sub">{h.sub}</div>}
                    {errorForm !== null && (
                        <div role="alert" className="rv-acceso__error">
                            <Icono nombre="circle-alert" />
                            <span>{errorForm}</span>
                        </div>
                    )}
                    {eleccion?.aviso === MENSAJES.otraCuenta && pantalla === 'biz' && (
                        <div role="alert" className="rv-acceso__error">
                            <Icono nombre="circle-alert" />
                            <span>{MENSAJES.otraCuenta}</span>
                        </div>
                    )}
                    {h.cuerpo}
                    {h.principal !== undefined && (
                        <button
                            type="button"
                            className="rv-acceso__principal"
                            aria-busy={ocupado}
                            disabled={h.principal.desactivado === true}
                            onClick={() => {
                                if (!ocupado) {
                                    h.principal?.accion();
                                }
                            }}
                        >
                            {ocupado ? (
                                <>
                                    <Asterisco />
                                    <span>{h.principal.ocupado}</span>
                                </>
                            ) : (
                                <>
                                    <span>{h.principal.texto}</span>
                                    <Icono nombre={h.principal.icono} />
                                </>
                            )}
                        </button>
                    )}
                    {h.enlaces !== undefined && (
                        <div className="rv-acceso__enlaces">
                            {h.enlaces.map((l) => (
                                <div key={l.texto}>
                                    {l.antes !== undefined && <span>{l.antes}</span>}
                                    <button type="button" className="rv-acceso__enlace" disabled={l.desactivado === true || l.accion === undefined} onClick={l.accion}>
                                        {l.texto}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {h.pie}
                </div>
            </div>
            <div aria-hidden="true" className="rv-acceso__hueco" style={{ marginBottom: -28 }} />
        </div>
    );
}
